import '@fortawesome/fontawesome-free/css/fontawesome.min.css';
import '@uppy/core/dist/style.css';
import '@uppy/progress-bar/dist/style.css';
import 'common/chartjs/register';
import 'whatwg-fetch';
import 'abortcontroller-polyfill';
import 'intersection-observer';
import 'trix/dist/trix';
import 'trix/dist/trix.css';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from 'styled-components';

import { concatTrendsFlexFilters } from 'common/utils/locationUtils';
import apolloClient from 'common/utils/apolloClient';
import IntlTranslationsProvider from 'common/components/IntlTranslationsProvider';
import styleTheme from 'common/constants/styleTheme';

import history from 'common/history';
import store from 'common/store';
import Routes from 'common/routes';
import customizeChartTooltips from 'trends/company/initializers';

import PositiveNotification from 'applications/positiveNotification/PositiveNotification';
import ActionsAndKudos from 'actions_and_kudos/containers/actions_and_kudos';
import { Nav } from 'common/components/Nav/Nav';
import UserActionsSidePanel from 'settings/UserActionsSidePanel';
import EditOwnProfileLauncher from 'settings/EditOwnProfileLauncher';

require('legacy/libs/bootstrap-daterangepicker/daterangepicker.css');

const isTrendsPage = window.location.href.includes('trends');

const Wrapper = ({ children }) => (
  <IntlTranslationsProvider>
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={styleTheme}>
          <Router history={history}>
            <CompatRouter>{children}</CompatRouter>
          </Router>
        </ThemeProvider>
      </ApolloProvider>
    </Provider>
  </IntlTranslationsProvider>
);
const Spa = () => (
  <Wrapper>
    <Routes />
  </Wrapper>
);
const HeaderNavBar = () => (
  <Wrapper>
    <Nav />
  </Wrapper>
);
const UserSettings = () => (
  <Wrapper>
    <UserActionsSidePanel />
  </Wrapper>
);

const EditOwnProfile = () => (
  <Wrapper>
    <EditOwnProfileLauncher />
  </Wrapper>
);

const Notifications = () => (
  <Wrapper>
    <PositiveNotification />
  </Wrapper>
);

store.dispatch({
  type: 'INITIALIZATION_DATA',
  data: window.initializationData || {},
});

// React 18's `createRoot(...).render(...)` schedules the initial render
// through the scheduler, so effects and DOM commits may not be flushed
// synchronously. Legacy Rails views and browser specs that drive them
// (jQuery interop, imperative post-mount side effects, Capybara that
// interacts before the next tick) rely on the React 17 guarantee that
// mount + effects complete before returning. `flushSync` restores that.
const renderSync = (container, element) => {
  const root = createRoot(container);
  flushSync(() => root.render(element));
};

document.addEventListener('DOMContentLoaded', () => {
  const userSettingsRoot = document.getElementsByClassName('user-settings-header')[0];
  if (userSettingsRoot) {
    renderSync(userSettingsRoot, <UserSettings />);
  }
  const editOwnProfileRoot = document.getElementsByClassName('js-edit-own-profile')[0];
  if (editOwnProfileRoot) {
    renderSync(editOwnProfileRoot, <EditOwnProfile />);
  }
  const navBarRoot = document.getElementsByClassName('js-nav-bar')[0];
  if (navBarRoot) {
    renderSync(navBarRoot, <HeaderNavBar />);
  }
  const spaRoot = document.getElementsByClassName('spa-root')[0];
  if (spaRoot) {
    renderSync(spaRoot, <Spa />);
    concatTrendsFlexFilters();
    customizeChartTooltips();
  }

  const positiveNotificationRoot = document.getElementsByClassName('js-positive-notification')[0];
  if (positiveNotificationRoot) {
    renderSync(positiveNotificationRoot, <Notifications />);
  }

  if (!isTrendsPage) {
    const actionsAndKudosRoot = document.getElementsByClassName('actions-and-kudos-container')[0];
    if (actionsAndKudosRoot) {
      renderSync(actionsAndKudosRoot, <ActionsAndKudos />);
    }
  }

});

if (module.hot) {
  module.hot.accept();
}
